<script lang="ts" setup>
import bus from '@/helper/Bus'
import { BusEvent } from '@/helper/Types'
import { onMounted, onUnmounted, ref } from 'vue'

const props = defineProps<{
  hasControls?: boolean
  hasNoCancel?: boolean
  isClosable?: boolean
  noPrimaryBtns?: boolean
  isEmbedded?: boolean
  confirmDisabled?: boolean
}>()

const COMPONENT_NAME = 'ModalComponent'
const btnprimary = ref<HTMLButtonElement>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'cancel'): void
  (e: 'confirm', payload?: any, params?: any): void
}>()

bus.$onC(COMPONENT_NAME, BusEvent.KEYPRESSED_ESC, () => emit('cancel'))
bus.$onC(COMPONENT_NAME, BusEvent.KEYPRESSED_ENTER, () => emit('confirm'))

onMounted((): void => {
  btnprimary.value?.focus()
})

onUnmounted((): void => {
  bus.$offC(COMPONENT_NAME)
})

function modalClicked(): void {
  emit('close')
}

function confirmClicked(e: MouseEvent): void {
  emit('confirm', undefined, {
    ctrlKey: e?.ctrlKey || e?.metaKey,
    shiftKey: e?.shiftKey
  })
}

function cancelClicked(): void {
  emit('cancel')
}
</script>

<template>
  <div class="modal" @click.self="modalClicked()">
    <div ref="btnprimary" class="modal__box" tabindex="1097">
      <div class="modal__content">
        <button v-if="props.isClosable" class="close-btn modal__btn-close" @click="$emit('close')">
          <i class="ic-times" />
        </button>

        <div class="modal__title h2">
          <slot name="title">
            {{ $t('modal.title') }}
          </slot>
        </div>

        <div class="modal__text">
          <slot :cancel="cancelClicked" name="text" />
        </div>

        <div v-if="props.hasControls" class="modal__btns">
          <button
            v-if="!props.hasNoCancel"
            class="btn is--secondary modal__btn-cancel"
            tabindex="1099"
            @click="cancelClicked"
          >
            <slot name="cancel">
              {{ $t('modal.btn_cancel') }}
            </slot>
          </button>

          <button
            :class="{ 'is--secondary': props.noPrimaryBtns }"
            :disabled="props.confirmDisabled"
            class="btn modal__btn-confirm"
            tabindex="1098"
            @click="(event) => confirmClicked(event)"
          >
            <slot name="confirm">
              {{ $t('modal.btn_confirm') }}
            </slot>
          </button>
        </div>
        <slot name="survey" />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@import '@/assets/style/helpers/include-media';

$modalPadding: 32;
$modalBtnRowHeight: 40;
$modalBtnMargin: 32;

.modal {
  display: flex;
  position: fixed;
  top: 0;
  left: 0;
  padding: 0 rem-calc($pagePadding);
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.7);
  z-index: 5;

  &.is--embedded {
    position: static;
    padding: 0;
    width: auto;
    height: auto;
    background: none;
  }

  &.is--copy {
    .form__element {
      width: rem-calc(376);
      margin-bottom: 0;

      label {
        text-align: left;
      }
    }
  }

  &__box {
    @include modalShadow();
    outline: none;
    padding: rem-calc($modalPadding);
    background: #fff;
    width: rem-calc(480);
    max-width: 100%;
    max-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;

    @include themify($themes) {
      border-radius: rem-calc(themed('cardBorderRadius'));
    }

    .top-badge {
      left: rem-calc(32);
    }

    .is--product-details & {
      width: rem-calc(934);
      padding-left: 0;
      padding-right: 0;

      .modal {
        &__content {
          width: 100%;
        }

        &__title {
          font-size: rem-calc(32);
          line-height: rem-calc(40);
          text-align: left;
          padding-left: rem-calc($modalPadding);
          padding-right: rem-calc($modalPadding);
          @include media('<tablet') {
            font-size: rem-calc(24);
            line-height: rem-calc(30);
            padding-left: 1.5rem;
            padding-right: 1.5rem;
          }
        }

        &__text {
          display: flex;
          margin-top: rem-calc(56);
          @include media('<tablet') {
            flex-wrap: wrap;
            margin-top: rem-calc(24);
          }

          & > * {
            flex-basis: 50%;
            flex-grow: 0;
            flex-shrink: 0;
            @include media('<tablet') {
              flex-basis: 100%;
            }
          }
        }

        &__btns {
          padding-left: rem-calc($modalPadding);
          padding-right: rem-calc($modalPadding);
          justify-content: space-between;
          @include media('<tablet') {
            margin-top: 0;
          }
        }
      }

      .check-list {
        padding: rem-calc(32);
        @include media('<tablet') {
          padding: 1rem
        }
      }
    }

    .is--large & {
      width: rem-calc(600);
      padding-top: rem-calc(40);
      padding-bottom: rem-calc(40);
    }

    .is--scheme & {
      width: rem-calc(600);
      padding-top: rem-calc(40);
      padding-bottom: rem-calc(40);
    }

    .is--status & {
      width: rem-calc(600);
      padding-top: rem-calc(40);
      padding-bottom: rem-calc(40);
    }

    .is--consent & {
      width: rem-calc(600);
      padding-top: rem-calc(40);
      padding-bottom: rem-calc(40);
    }

    .is--copy & {
      width: rem-calc(600);
    }

    .is--info & {
      width: rem-calc(934);
      padding: rem-calc($modalPadding) 0 0 0;
      text-align: left;
    }

    .is--update & {
      width: rem-calc(800);
    }

    .is--download-configuration & {
      width: rem-calc(600);

      .download-configuration__title {
        margin-top: rem-calc(32);
      }

      .report-type-select {
        .form__input-wrapper {
          display: block;
        }

        .fTable {
          margin-top: rem-calc(12);

          @include themify($themes) {
            border-radius: rem-calc(themed('cardBorderRadius'));
          }

          .form__checkbox {
            display: flex;
            height: 100%;
            position: absolute;
            width: rem-calc(64);
            justify-content: center;

            .form__cm {
              position: static;
              margin: 0;
            }
          }

          .fTable__item:last-child {
            border-bottom: none;
          }

          .column {
            padding-left: rem-calc(64) !important;
          }

          .report-type-select__headline {
            font-size: rem-calc(18);
            line-height: rem-calc(28);
            margin: 0;
            flex: 1;
          }

          .report-type-select__text {
            font-size: rem-calc(14);
            line-height: rem-calc(17);
          }
        }
      }
    }
  }

  &__title {
    text-align: center;
    line-height: rem-calc(32);
    color: $textColor;
    margin-bottom: 0;

    &.h2 {
      color: $textColor;
    }

    .is--info & {
      width: 100%;
      padding-left: rem-calc($modalPadding);
      padding-right: rem-calc($modalPadding);
      text-align: left;
      margin-bottom: rem-calc(16);

      &.h2 {
        font-size: rem-calc(32);
        line-height: rem-calc(40);
      }
    }

    .is--consent & {
      width: 100%;

      &.h2 {
        font-size: rem-calc(32);
        text-align: left;
      }
    }

    .is--error & {
      @include themify($themes) {
        color: $errorColor;
      }
    }

    .is--update & {
      display: none;
    }
  }

  &__content {
    max-width: 100%;

    .is--info & {
      display: flex;
      flex-direction: column;
      max-height: calc(100vh - 2rem);
    }

    .is--update & {
      width: 100%;
    }

    .is--download-configuration & {
      width: 100%;
    }
  }

  &__text {
    margin-top: rem-calc(32);
    margin-bottom: 0;
    text-align: center;

    .is--scheme & {
      text-align: left;
      margin-top: rem-calc(14);

      & > p {
        text-align: center;
        margin-bottom: rem-calc(24);
      }
    }

    .is--copy & {
      width: 100%;

      .form__element {
        margin-bottom: 0;
      }
    }

    .is--status & {
      margin-top: rem-calc(40);
    }

    .is--consent & {
      margin-top: rem-calc(16);
      text-align: left;
    }

    .is--info & {
      overflow-y: hidden;
      overflow-x: hidden;
      text-align: left;
      margin-top: 0;
      display: flex;
      flex-direction: column;
    }

    .is--update & {
      $maxTextHeight: rem-calc($modalPadding * 2 + $modalBtnRowHeight + $modalBtnMargin);
      margin-top: 0;
      max-height: calc(100vh - #{$maxTextHeight});
      display: flex;
      flex-direction: column;
    }

    .info__content {
      overflow-y: auto;
      overflow-x: hidden;
    }

    .is--download-configuration & {
      text-align: left;

      .download-configuration__box {
        padding: rem-calc(16);

        .--box-headline {
          font-weight: bold;
        }
      }

      .fTable.is--products-table .fTable__item {
        border-bottom: 0;
      }

      .download-configuration__field {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: rem-calc(16);
        gap: 1rem;

        .span {
          margin-bottom: rem-calc(16);
        }

        .form__element {
          margin-bottom: 0;
          width: 55%;
          flex: 1;

          .form__input-wrapper {
            display: block;
          }
        }
      }
    }
  }

  &__btns {
    margin-top: rem-calc($modalBtnMargin);
    height: rem-calc(40);
    display: flex;
    flex-shrink: 0;
    width: 100%;
    align-items: center;
    justify-content: center;

    .is--info & {
      height: rem-calc(72);
      @include componentShadow();
      margin-top: 0;
    }

    .is--status & {
      margin-top: rem-calc(40);
    }

    .is--consent & {
      margin-top: rem-calc(40);
    }

    & > .btn {
      height: rem-calc(40);
      min-width: rem-calc(180);
      padding-left: rem-calc(20);
      padding-right: rem-calc(20);
      margin-right: rem-calc(16);

      @include media('<tablet') {
        min-width: auto;
      }

      &:last-child {
        margin-right: 0;
      }
    }
  }

  &__btn-close {
    position: absolute;
    top: rem-calc(12);
    right: rem-calc(12);
  }

  &__icon {
    margin: 0 auto rem-calc(40) auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: rem-calc(64);
    height: rem-calc(64);
    border-radius: 50%;
    @include themify($themes) {
      background-color: themed('primaryColor');
    }

    .is--error & {
      background: $errorColor;
    }

    .loading-icon {
      height: rem-calc(32);
      width: rem-calc(32);
      display: block;
    }

    .is--loading {
      animation: spin 1.3s infinite cubic-bezier(0.5, 0.1, 0.5, 0.9);
    }

    i[class^='ic-'],
    i[class*=' ic-'] {
      font-size: rem-calc(47);
      line-height: rem-calc(47);
      height: rem-calc(47);
      width: rem-calc(47);
      display: block;
      color: #fff;

      &.is--loading {
        animation: spin 1.3s infinite cubic-bezier(0.5, 0.1, 0.5, 0.9);
        /*transform-origin: rem-calc(16) rem-calc(16);*/
        font-size: rem-calc(32);
        line-height: rem-calc(32);
        height: rem-calc(32);
        width: rem-calc(32);

        &:before {
          font-size: 32px;
        }
      }

      &.ic-check {
        font-size: rem-calc(47);
      }
    }
  }

  &__product-image {
    height: 100%;

    .image-box {
      padding-bottom: 75%;
    }
  }

  &__subtitle {
    margin-bottom: rem-calc(16);
  }

  &__update-products-list {
    position: relative;
    flex-grow: 1;
    flex-shrink: 1;
    overflow-y: auto;
  }

  &__update-product {
    display: flex;
    margin-bottom: rem-calc(32);

    &:last-child {
      margin-bottom: 4px; //4px fixes to scrollbar showing up although not needed issue in Firefox and Safari
    }
  }

  &__update-image {
    width: rem-calc(100);
    height: rem-calc(92);
    flex-grow: 0;
    flex-shrink: 0;
    flex-basis: rem-calc(100);

    img {
      height: 100%;
      width: 100%;
      object-fit: cover;
    }
  }

  &__update-description {
    margin-bottom: rem-calc(24);
  }

  &__update-data {
    padding: 0 rem-calc(16);
    flex-grow: 1;
    text-align: left;
  }

  &__update-status {
    display: flex;
    flex-direction: column;
    justify-content: center;
    text-align: right;
    margin-right: 0;
  }

  &__new-price {
    @include themify($themes) {
      font-weight: bold;
      color: themed('primaryColor');
    }
  }

  &__update-warning {
    display: flex;
    padding: rem-calc(16);
    margin-top: rem-calc(32);
    @include themify($themes) {
      border-radius: rem-calc(themed('inputBorderRadius'));
      background-color: rgba(themed('secondaryColor'), 0.1);
    }
  }

  &__update-warning-icon {
    margin-right: rem-calc(12);
    flex-basis: rem-calc(28);
    flex-grow: 0;
    flex-shrink: 0;
    width: rem-calc(28);

    i[class^='ic-']:before,
    i[class*=' ic-']:before,
    i.ic:before {
      font-size: rem-calc(28);
    }

    @include themify($themes) {
      color: themed('secondaryColor');
    }
  }

  &__update-warning-text {
    text-align: left;

    .span {
      font-size: rem-calc($textMidSize);
    }

    .form__checkbox {
      margin-top: rem-calc(8);
      font-size: rem-calc($textMidSize);
    }

    @include themify($themes) {
      .form__cm {
        border-color: themed('secondaryColor');
      }

      input[type='checkbox']:checked ~ .form__cm {
        background: themed('secondaryColor');
        border-color: themed('secondaryColor');
      }
    }
  }
}
</style>
